/**
 * WHAT A CAREER CARRIES ACROSS A WINTER — one object, so nothing is forgotten.
 *
 * Before 4 Oct 2026 the only things that crossed a winter were the roster, the
 * garage and a seasons-run counter. Everything else was wiped: every sponsor
 * contract and renewal decision (D-EZ-91), every shop's bank, every grudge and
 * every injury (D-EZ-94). This is the rest of it, produced by the winter
 * (`OffseasonResult.carry`) and handed to the next spring
 * (`RunOptions.carry` → `beginSeason({ carryIn })` and `runSeasonFinance({ carry })`).
 *
 * ⚠ FOR WHOEVER SAVES A CAREER: this object is the fourth thing a saved
 * league must keep beside `roster`, `teams` and `seasonsRun`. A load that drops
 * it starts the next season with fresh sponsors, empty banks and no grudges —
 * not broken, but not the league that was saved.
 */

import type { FinanceCarry } from './finance/seasonFinance.ts';
import { EMPTY_FINANCE_CARRY } from './finance/seasonFinance.ts';
import type { SeasonCarryIn } from './seasonPlay.ts';
import type { CareerPrior } from './history/types.ts';

export const CAREER_CARRY_VERSION = 1;

export interface CareerCarry {
  version: number;
  /** Contracts, shop banks, drivers' own money (D-EZ-91). */
  finance: FinanceCarry;
  /** Feuds, injuries, suspensions still owed (D-EZ-94). */
  season: SeasonCarryIn;
  /**
   * D-EZ-97 — careers that began before this league did (the 2025 scenario's
   * real drivers). Kept for every driver ever given one, retired or not, so the
   * record book can add them in.
   */
  priors: Record<string, CareerPrior>;
}

export function emptyCareerCarry(priors: Record<string, CareerPrior> = {}): CareerCarry {
  return {
    version: CAREER_CARRY_VERSION,
    finance: { ...EMPTY_FINANCE_CARRY, contracts: {}, shopBankMb: {}, driverWealthMb: {} },
    season: { feuds: [], suspended: [], injured: [] },
    priors: { ...priors },
  };
}
